import { ITariffDiscount } from "@interfaces/admin/tariff";
import { IUser } from "@interfaces/admin/user";
import { IUserProxy } from "@interfaces/admin/userProxy";
import { IUserPurchaseHistoryRecord } from "@interfaces/admin/userPurchaseHistoryRecord";
import { authApi } from "@RTKQuery/basic";

export const customersAuthApi = authApi.injectEndpoints({
    endpoints: (build) => ({
        getAllUsers: build.query<{
            users: IUser[],
            count_item: number
        }, {
            page: number,
            limit: number,
            is_active: "all" | "active",
            search: string
        }>({
            query: (params) => ({
                url: "users",
                params: Object.assign(
                    {
                        page: params.page,
                        size: params.limit
                    },
                    params.is_active === "active" ? {
                        is_active: true
                    } : {},
                    params.search ? {
                        user_id: Number(params.search)
                    } : {},
                )
            }),
            providesTags: ["UserAdmin"]
        }),
        getUserPurchaseHistory: build.query<{
            purchases_history: IUserPurchaseHistoryRecord[],
            count_items: number
        }, {
            user_id: IUser["id"],
            page: number,
            limit: number
        }>({
            query: ({user_id, ...params}) => ({
                url: `users/${user_id}/history`,
                params: {
                    page: params.page,
                    size: params.limit
                }
            }),
            providesTags: (_result, _error, { user_id }) => [{ type: "PurchaseHistoryAdmin", id: user_id }]
        }),
        getUserProxies: build.query<{
            purchases: IUserProxy[],
            count_items: number
        }, {
            user_id: IUser["id"],
            page: number,
            limit: number
        }>({
            query: ({user_id, ...params}) => ({
                url: `users/${user_id}/rented_now`,
                params: {
                    page: params.page,
                    size: params.limit
                }
            }),
            providesTags: ["ProxyUserAdmin"]
        }),
        updateUsersRentTime: build.mutation<boolean, {
            days: number,
            hours: number,
            user_ids?: IUser["id"][],
            is_active?: boolean
        }>({
            query: (body) => ({
                url: "users/group_update_time",
                method: "PUT",
                body
            }),
            invalidatesTags: ["ProxyUserAdmin"]
        }),
        grantProxy: build.mutation<{
            granted: boolean,
            router_id: number,
            days: number,
            end_date: string,
            charged: number,
            balance_after: number,
            node_notified: boolean
        }, {
            user_id: IUser["id"],
            router_id: number,
            days: number,
            charge: boolean
        }>({
            query: ({ user_id, ...body }) => ({
                url: `grant/user/${user_id}/grant`,
                method: "POST",
                body
            }),
            // A completed grant also adds a purchase-history entry.
            invalidatesTags: (_result, error, { user_id }) => error ? [] : [
                "ProxyUserAdmin", "UserAdmin", { type: "PurchaseHistoryAdmin", id: user_id },
                { type: "PurchaseHistoryAdmin", id: "ANALYTICS" }
            ]
        }),
        updateUsersBalance: build.mutation<boolean, {
            amount: number,
            user_ids?: IUser["id"][],
            is_active?: boolean
        }>({
            query: (body) => ({
                url: "users/group_update_balance",
                method: "PUT",
                body
            }),
            invalidatesTags: ["UserAdmin"]
        }),
        getPersonalTariffs: build.query<ITariffDiscount[], {
            user_id: IUser["id"]
        }>({
            query: ({user_id}) => `users/${user_id}/personal_tariffs`,
            providesTags: ["PersonalUserTariffsAdmin"]
        }),
        switchWholesalerStatus: build.mutation<boolean, {
            user_id: IUser["id"],
            status: boolean
        }>({
            query: ({user_id, ...body}) => ({
                url: `users/${user_id}/update_wholesaler`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["PersonalUserTariffsAdmin", "UserAdmin"],
        }),
        updatePersonalPrice: build.mutation<boolean, {
            id: ITariffDiscount["personal_prices"][0]["id"],
            price: number
        }[]>({
            query: (body) => ({
                url: "users/update_personal_prices",
                method: "PUT",
                body
            }),
            invalidatesTags: ["PersonalUserTariffsAdmin"],
        }),
        switchPercentStatus: build.mutation<boolean, {
            personal_price_id: ITariffDiscount["personal_prices"][0]["id"],
            new_status: boolean,
            params: {
                user_id: IUser["id"]
            }
        }>({
            query: ({personal_price_id, new_status}) => ({
                url: `users/${personal_price_id}/is_percent_status`,
                method: "PUT",
                body: {
                    new_status
                }
            }),
            async onQueryStarted({params, personal_price_id, new_status}, {dispatch, queryFulfilled}) {
                try {
                    await queryFulfilled
                    
                    dispatch(customersAuthApi.util.updateQueryData("getPersonalTariffs", params, (draft) => {
                        draft.forEach(item => {
                            item.personal_prices.forEach(i => {
                                if (i.id === personal_price_id) {
                                    i.is_percent = new_status
                                }
                            })
                        })
                    }))
                } catch {}
            },
        })
    }),
    overrideExisting: false,
})

export const {
    useGetAllUsersQuery,
    useUpdateUsersBalanceMutation,
    useUpdateUsersRentTimeMutation,
    useGrantProxyMutation,
    useGetUserProxiesQuery,
    useGetUserPurchaseHistoryQuery,

    useGetPersonalTariffsQuery,
    useSwitchWholesalerStatusMutation,
    useUpdatePersonalPriceMutation,
    useSwitchPercentStatusMutation
} = customersAuthApi;
